/**
 * P2-5a Safety Reflex desk Soft≠ invent — Condition / Reflex, not second brain.
 * Tenant CMC admin. Not /sa/*.
 */
import { useMemo, useState } from "react";
import { html, Card, Field, Input, Alert, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";

function safeReflexPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    /* */
  }
  if (op === "get__ai_safety_reflex") return "/ai/safety/reflex";
  if (op === "post__ai_safety_conditions") return "/ai/safety/conditions";
  if (op === "post__ai_safety_reflex") return "/ai/safety/reflex";
  if (op === "get__ai_safety_reflex_p_effective") {
    const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
    return `/ai/safety/reflex/${encodeURIComponent(id)}/effective`;
  }
  return "";
}

export function SafetyReflexPanel({ toast }) {
  const list = useApi(safeReflexPath("get__ai_safety_reflex"));
  const { run, busy, envelope } = useAction();
  const items = useMemo(() => (list.data && list.data.items) || [], [list.data]);
  const conditions = useMemo(() => (list.data && list.data.conditions) || [], [list.data]);
  const [name, setName] = useState("");
  const [eventType, setEventType] = useState("alarm.trigger");
  const [condName, setCondName] = useState("");
  const [condPick, setCondPick] = useState("");
  const [effective, setEffective] = useState(null);
  const condId = condPick && conditions.some((c) => String(c.condition_id || "") === condPick)
    ? condPick
    : String((conditions[0] && conditions[0].condition_id) || "");

  const addCondition = async () => {
    if (!condName.trim()) return toast && toast("Condition name required", "warn");
    try {
      const out = await run(safeReflexPath("post__ai_safety_conditions"), {
        method: "POST",
        body: { name: condName.trim(), event_type: eventType.trim() || "alarm.trigger" },
      });
      if (!out || out.ok === false) {
        toast && toast("Condition refused", "warn");
        return;
      }
      toast && toast("Condition defined", "go");
      setCondName("");
      list.reload && list.reload();
    } catch {
      toast && toast("Condition failed", "bad");
    }
  };

  const addReflex = async () => {
    if (!name.trim()) return toast && toast("Reflex name required", "warn");
    if (!condId) {
      toast && toast("Condition required — Reflex without a condition never fires", "warn");
      return;
    }
    try {
      const out = await run(safeReflexPath("post__ai_safety_reflex"), {
        method: "POST",
        body: {
          name: name.trim(),
          condition_id: condId,
          actions: ["needs_you_internal_notify"],
          blast_boundary: { max_targets: 1, allow_external_message: false },
        },
      });
      if (!out || out.ok === false) {
        toast && toast("Reflex refused — AI cannot own Live Reflex", "warn");
        return;
      }
      toast && toast("Safety Reflex defined · human owner", "go");
      setName("");
      list.reload && list.reload();
    } catch {
      toast && toast("Reflex failed", "bad");
    }
  };

  const showEffective = async (rid) => {
    try {
      const out = await run(
        safeReflexPath("get__ai_safety_reflex_p_effective", { pathParams: [rid] }),
        { method: "GET" },
      );
      setEffective(out || null);
    } catch {
      toast && toast("Effective read failed", "bad");
    }
  };

  return html`<div className="safety-reflex-panel" data-cmc-reflex="1" data-soft-invent="0">
    <${Card} title="Safety Reflex" meta="condition · blast · effective version"
      sub="Safety priority ≠ decision brain. AI cannot silently own Reflex.">
      <${Alert} tone="warn" title="Not a second recommendation brain"
        say="Deterministic emergency only. Business plan stays with AI Decision Contract + Gateway."/>
      <${KV} k="Reflexes" v=${list.loading ? "…" : String(items.length)}/>
      <${KV} k="Conditions" v=${list.loading ? "…" : String(conditions.length)}/>
      <${KV} k="AI can own" v="false"/>
      <${KV} k="Blast ceiling" v="max_targets=1 · external=false"/>
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <${Field} label="Event condition">
        <${Input} value=${condName} onChange=${(e) => setCondName(e.target.value)}
          placeholder="Door forced"/>
      <//>
      <${Field} label="Event type">
        <${Input} value=${eventType} onChange=${(e) => setEventType(e.target.value)}/>
      <//>
      <button type="button" className="btn btn--ghost btn--sm" disabled=${busy}
        onClick=${addCondition} style=${{ marginTop: 6 }}>Add condition</button>
      <${Field} label="Bind condition">
        <select className="input" value=${condId}
          onChange=${(e) => setCondPick(e.target.value)}
          data-cmc-reflex-condition="1">
          ${!conditions.length
            ? html`<option value="">Add a condition first</option>`
            : conditions.map((c) => {
                const id = String(c.condition_id || "");
                return html`<option key=${id} value=${id}>${c.name || id}</option>`;
              })}
        </select>
      <//>
      ${!conditions.length
        ? html`<p className="note">Add a condition first. Reflex without a condition never fires.</p>`
        : null}
      <${Field} label="Reflex name">
        <${Input} value=${name} onChange=${(e) => setName(e.target.value)}
          placeholder="Site siren bound"/>
      <//>
      <button type="button" className="btn btn--go btn--sm"
        disabled=${busy || !name.trim() || !condId}
        onClick=${addReflex} style=${{ marginTop: 6 }}>Define human Reflex</button>
      <div className="rows" style=${{ marginTop: 10 }}>
        ${items.map((it) => html`
          <button key=${it.reflex_id} type="button" className="row"
            onClick=${() => showEffective(it.reflex_id)}>
            <span className="main">
              <b>${it.name}</b>
              <span>v${it.version} · owner ${it.owner_kind} · blast ${it.blast_boundary && it.blast_boundary.max_targets != null ? it.blast_boundary.max_targets : 1} · brain=false</span>
            </span>
          </button>`)}
      </div>
      ${effective ? html`<p className="note" style=${{ marginTop: 8 }}>
        Effective · v${effective.version} · enabled=${String(!!effective.enabled)} · decision_brain=false
      </p>` : null}
    <//>
  </div>`;
}

export default SafetyReflexPanel;
